import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import storageSession from "redux-persist/lib/storage/session";
import { put, select, takeLatest } from "redux-saga/effects";
import {
  logout as apiLogout,
  getUserByToken,
  redirectToCommunity,
} from "./authCrud";
import { clearCache } from "../../../services/reactQuery";
import { autosaveAdapter } from "../../../pages/AtualizacaoCompleto/utils/autosaveAdapter";
import { clearFeatureFlagCache } from "../../../hooks/useFeatureFlag";

export const actionTypes = {
  Login: "[Login] Action",
  LoginCommunity: "[LoginCommunity] Action",
  ImpersonateLogin: "[ImpersonateLogin] Action",
  LogoutRequested: "[Logout Requested] Action",
  Logout: "[Logout] Action",
  Register: "[Register] Action",
  UserRequested: "[Request User] Action",
  UserLoaded: "[Load User] Auth API",
  SetUser: "[Set User] Action",
  SetOrigin: "[Set Origin] Action",
  UpdateUserProvider: "[Update User Provider] Action",
  SetUserLoginId: "[Set User Login Id] Action",
  TokensRefreshed: "[Tokens Refreshed] Action",
};

export function isAuthServiceSession(auth) {
  return typeof auth?.idToken === "string" && auth.idToken !== "";
}

function accessTokenExpiresAtFromSeconds(expiresIn) {
  if (typeof expiresIn !== "number" || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    return undefined;
  }
  return Date.now() + Math.floor(expiresIn * 1000);
}

const initialAuthState = {
  user: undefined,
  authToken: undefined,
  origin: { type: "default" },
  userLoginId: undefined,
  impersonateExpiresAt: undefined,
  impersonateAdminEmail: undefined,
  idToken: undefined,
  accessTokenExpiresAt: undefined,
};

export const reducer = persistReducer(
  {
    storage,
    key: "v713-demo1-auth",
    whitelist: [
      "user",
      "authToken",
      "origin",
      "userLoginId",
      "impersonateExpiresAt",
      "impersonateAdminEmail",
      "idToken",
      "accessTokenExpiresAt",
    ],
  },
  (state = initialAuthState, action) => {
    switch (action.type) {
      case actionTypes.Login: {
        const { authToken, idToken, expiresIn } = action.payload;
        return {
          authToken,
          idToken: idToken ?? undefined,
          accessTokenExpiresAt: accessTokenExpiresAtFromSeconds(expiresIn),
          user: undefined,
          origin: { type: "default" },
          userLoginId: state.userLoginId,
        };
      }

      case actionTypes.TokensRefreshed: {
        const { authToken, idToken, expiresIn } = action.payload;
        return {
          ...state,
          authToken,
          idToken: typeof idToken === "string" && idToken ? idToken : state.idToken,
          accessTokenExpiresAt: accessTokenExpiresAtFromSeconds(expiresIn),
        };
      }
      case actionTypes.ImpersonateLogin: {
        const { authToken, expiresAt, adminEmail, userLoginId } = action.payload;

        storageSession.removeItem("persist:subscription");

        return {
          ...initialAuthState,
          authToken,
          userLoginId,
          impersonateExpiresAt: expiresAt,
          impersonateAdminEmail: adminEmail,
        };
      }
      case actionTypes.LoginCommunity: {
        const { authToken } = action.payload;
        return {
          authToken,
          user: undefined,
          origin: { type: "default" },
          userLoginId: state.userLoginId,
        };
      }
      case actionTypes.Register: {
        const { authToken } = action.payload;
        return {
          authToken,
          user: undefined,
          origin: { type: "default" },
          userLoginId: state.userLoginId,
        };
      }

      case actionTypes.Logout: {
        storageSession.removeItem("persist:subscription");
        return initialAuthState;
      }

      case actionTypes.UserLoaded: {
        const { user } = action.payload;
        return { ...state, user };
      }

      case actionTypes.SetUser: {
        const { user } = action.payload;
        return { ...state, user: { ...state.user, ...user } };
      }

      case actionTypes.SetOrigin: {
        if (action.payload) {
          return { ...state, origin: { type: action.payload } };
        }
        return { ...state };
      }

      case actionTypes.UpdateUserProvider: {
        const { provider } = action.payload;
        return {
          ...state,
          provider,
        };
      }

      case actionTypes.SetUserLoginId: {
        const { loginId } = action.payload;

        return { ...state, userLoginId: loginId };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: (authToken, session) => ({
    type: actionTypes.Login,
    payload: {
      authToken,
      idToken: session?.idToken,
      expiresIn: session?.expiresIn,
    },
  }),
  refreshTokens: (authToken, expiresIn, idToken) => ({
    type: actionTypes.TokensRefreshed,
    payload: { authToken, expiresIn, idToken },
  }),
  impersonateLogin: (authToken, expiresAt, adminEmail, userLoginId) => ({
    type: actionTypes.ImpersonateLogin,
    payload: { authToken, expiresAt, adminEmail, userLoginId },
  }),
  loginCommunity: authToken => ({
    type: actionTypes.LoginCommunity,
    payload: { authToken },
  }),
  register: authToken => ({
    type: actionTypes.Register,
    payload: { authToken },
  }),
  logout: () => ({ type: actionTypes.Logout }),
  logoutRequested: () => ({ type: actionTypes.LogoutRequested }),
  requestUser: user => ({
    type: actionTypes.UserRequested,
    payload: { user },
  }),
  fulfillUser: user => ({ type: actionTypes.UserLoaded, payload: { user } }),
  setUser: user => ({ type: actionTypes.SetUser, payload: { user } }),
  setOrigin: origin => ({ type: actionTypes.SetOrigin, payload: { origin } }),
  updateUserProvider: provider => ({
    type: actionTypes.UpdateUserProvider,
    payload: { provider },
  }),
  setUserLoginId: loginId => ({
    type: actionTypes.SetUserLoginId,
    payload: { loginId },
  }),
};

export function* impersonateLoginSaga() {
  clearCache();
  yield put(actions.requestUser());
}

export function* userRequested({ payload }) {
  try {
    const { data: user } = yield getUserByToken();

    yield put(actions.fulfillUser(user));

    if (payload?.callback) {
      payload.callback(user);
    }
  } catch (error) {
    yield put(actions.logout());
  }
}

export function* logoutRequestedSaga() {
  try {
    const state = yield select();
    const hasLegacyLogin = !isAuthServiceSession(state.auth) && Boolean(state.auth.userLoginId);

    if (hasLegacyLogin) {
      yield apiLogout(state.auth.userLoginId);
    }
  } catch (err) {}
  yield put(actions.logout());
}

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.ImpersonateLogin, impersonateLoginSaga);

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, userRequested);

  yield takeLatest(actionTypes.LoginCommunity, function* loginCommunitySaga() {
    const queryParams = window.location.search;

    redirectToCommunity(queryParams).then(({ data: { url } }) => {
      window.location.replace(url);
    });

    yield;
  });

  yield takeLatest(actionTypes.LogoutRequested, logoutRequestedSaga);

  yield takeLatest(actionTypes.Logout, function* logoutSaga() {
    clearCache();
    // Fase 2 (JusCalc): rascunhos de autosave não sobrevivem ao logout.
    autosaveAdapter.limparTodos();
    // O cache de flags é de módulo: sem limpar, o próximo usuário herdaria as
    // RELEASE/KILL_SWITCH resolvidas para o usuário anterior.
    clearFeatureFlagCache();
    yield;
  });
}
